import { useState } from "react";
import { parse, isPast, isToday } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Trash2, Loader2, CheckCircle, XCircle, Eye, Edit,
  CalendarDays, Clock, MapPin, Mail, User, Plus, ExternalLink,
} from "lucide-react";
import { EventDescription } from "@/components/EventDescription";
import { EventFormModal } from "@/components/EventFormModal";
import { normalizeUrl } from "@/lib/urlUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tryParseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    let d = parse(dateStr, "MMMM d, yyyy", new Date());
    if (!isNaN(d.getTime())) return d;
    d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
  } catch { /* ignore */ }
  return null;
};

const isEventUpcoming = (dateStr) => {
  const d = tryParseDate(dateStr);
  if (!d) return true;
  return isToday(d) || !isPast(d);
};

const getTimeMinutes = (timeStr) => {
  const match = String(timeStr || "").match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return 0;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3].toUpperCase();
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return hour * 60 + minute;
};

const getEventTimestamp = (event) => {
  const date = tryParseDate(event.date);
  if (!date) return Number.POSITIVE_INFINITY;
  const sortedDate = new Date(date);
  sortedDate.setHours(0, getTimeMinutes(event.time), 0, 0);
  return sortedDate.getTime();
};

const sortEventsChronologically = (items) => [...items].sort((a, b) => {
  return getEventTimestamp(a) - getEventTimestamp(b);
});

const formatEventTimeRange = (event) => {
  if (event.time && event.end_time) return `${event.time} - ${event.end_time}`;
  return event.time || "";
};

export const EventManagement = ({ events, token, onRefresh, businesses = [], markets = [] }) => {
  const [activeTab, setActiveTab] = useState("pending");
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [viewModal, setViewModal] = useState({ open: false, event: null });
  const [editModal, setEditModal] = useState({ open: false, event: null });
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const handleEventAction = async (eventId, action) => {
    setUpdatingStatus(eventId);
    try {
      await axios.post(`${API}/admin/event/${eventId}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Event ${action}d successfully`);
      onRefresh();
    } catch { toast.error(`Failed to ${action} event`); }
    finally { setUpdatingStatus(null); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/admin/event/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Event deleted");
      onRefresh();
    } catch { toast.error("Failed to delete event"); }
  };

  const openEdit = (event) => {
    setEditModal({ open: true, event });
  };

  // Categorize events
  const pendingEvents = events.filter(e => e.approval_status === "pending");
  const deniedEvents = events.filter(e => e.approval_status === "rejected");
  const approvedEvents = events.filter(e => e.approval_status === "approved");
  const upcomingEvents = approvedEvents.filter(e => isEventUpcoming(e.date));
  const pastEvents = approvedEvents.filter(e => !isEventUpcoming(e.date));

  const tabCounts = {
    pending: pendingEvents.length,
    upcoming: upcomingEvents.length,
    past: pastEvents.length,
    denied: deniedEvents.length,
  };

  const filteredEvents = sortEventsChronologically(
    activeTab === "pending" ? pendingEvents :
    activeTab === "upcoming" ? upcomingEvents :
    activeTab === "past" ? pastEvents :
    deniedEvents
  );

  return (
    <div>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Manage Events</h1>
        <Button onClick={() => setCreateModalOpen(true)} className="bg-[#1a1a1a] hover:bg-slate-800 text-white" data-testid="admin-add-event-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Event
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Pending", count: tabCounts.pending, bg: "bg-yellow-100", text: "text-yellow-600", icon: Clock },
          { label: "Upcoming", count: tabCounts.upcoming, bg: "bg-green-100", text: "text-green-600", icon: CalendarDays },
          { label: "Past", count: tabCounts.past, bg: "bg-slate-100", text: "text-slate-600", icon: CalendarDays },
          { label: "Denied", count: tabCounts.denied, bg: "bg-red-100", text: "text-red-600", icon: XCircle },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${s.bg} rounded-lg flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.text}`} />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900">{s.count}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="border-b border-slate-100">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start rounded-none bg-transparent border-0 p-0">
              {[
                { value: "pending", label: `Pending (${tabCounts.pending})`, border: "border-yellow-500" },
                { value: "upcoming", label: `Upcoming (${tabCounts.upcoming})`, border: "border-green-500" },
                { value: "past", label: `Past (${tabCounts.past})`, border: "border-slate-500" },
                { value: "denied", label: `Denied (${tabCounts.denied})`, border: "border-red-500" },
              ].map((t) => (
                <TabsTrigger key={t.value} value={t.value} className={`rounded-none border-b-2 border-transparent data-[state=active]:${t.border} data-[state=active]:bg-transparent px-6 py-3`} data-testid={`admin-event-tab-${t.value}`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No {activeTab} events.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Event Title</TableHead>
                <TableHead className="font-semibold">Submitted By</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.map((event) => (
                <TableRow key={event.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">{event.title}</TableCell>
                  <TableCell className="text-gray-600">{event.submitted_by_name || event.submitted_by_email}</TableCell>
                  <TableCell><Badge variant="secondary" className="bg-gray-100 text-gray-700 capitalize">{event.submitted_by_type}</Badge></TableCell>
                  <TableCell>{event.date || "-"}</TableCell>
                  <TableCell>{event.location || "-"}</TableCell>
                  <TableCell>
                    <Badge className={event.approval_status === "pending" ? "bg-yellow-100 text-yellow-800" : event.approval_status === "rejected" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>
                      {event.approval_status || "pending"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setViewModal({ open: true, event })} className="text-slate-600 hover:text-slate-900" data-testid={`admin-view-event-${event.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {event.approval_status === "pending" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleEventAction(event.id, "approve")} disabled={updatingStatus === event.id} className="text-green-600 hover:bg-green-50" data-testid={`admin-approve-event-${event.id}`}>
                            {updatingStatus === event.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEventAction(event.id, "reject")} disabled={updatingStatus === event.id} className="text-red-500 hover:bg-red-50" data-testid={`admin-reject-event-${event.id}`}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {event.approval_status === "rejected" && (
                        <Button variant="ghost" size="sm" onClick={() => handleEventAction(event.id, "approve")} className="text-green-600 hover:bg-green-50"><CheckCircle className="w-4 h-4" /></Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(event)} className="text-slate-600 hover:text-slate-900" data-testid={`admin-edit-event-${event.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteEvent(event.id)} className="text-red-500 hover:bg-red-50" data-testid={`admin-delete-event-${event.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* View Event Modal */}
      <Dialog open={viewModal.open} onOpenChange={(open) => !open && setViewModal({ open: false, event: null })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Event Details</span>
              <Badge className={viewModal.event?.approval_status === "pending" ? "bg-yellow-100 text-yellow-800" : viewModal.event?.approval_status === "rejected" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>
                {viewModal.event?.approval_status || "pending"}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          {viewModal.event && (
            <div className="space-y-5 mt-4">
              <h3 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{viewModal.event.title}</h3>
              {viewModal.event.description && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Description</p>
                  <EventDescription text={viewModal.event.description} className="text-slate-700 leading-relaxed" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Date", value: viewModal.event.date, icon: CalendarDays },
                  { label: "Time", value: formatEventTimeRange(viewModal.event), icon: Clock },
                  { label: "Location", value: viewModal.event.location, icon: MapPin },
                  { label: "Event Link", value: viewModal.event.event_link, icon: ExternalLink },
                  { label: "Submitted By", value: viewModal.event.submitted_by_name || viewModal.event.submitted_by_email, icon: User },
                  { label: "Contact", value: viewModal.event.submitted_by_email, icon: Mail },
                  { label: "Type", value: viewModal.event.submitted_by_type },
                ].map((f) => (
                  <div key={f.label}>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      {f.icon && <f.icon className="w-3 h-3" />} {f.label}
                    </p>
                    {f.label === "Event Link" && f.value ? (
                      <a href={normalizeUrl(f.value)} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline">
                        {f.value}
                      </a>
                    ) : (
                      <p className="text-slate-900 capitalize">{f.value || "-"}</p>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2 pt-4 border-t">
                {viewModal.event.approval_status === "pending" && (
                  <>
                    <Button variant="outline" onClick={() => { handleEventAction(viewModal.event.id, "reject"); setViewModal({ open: false, event: null }); }} className="text-red-600 border-red-200 hover:bg-red-50"><XCircle className="w-4 h-4 mr-2" />Reject</Button>
                    <Button onClick={() => { handleEventAction(viewModal.event.id, "approve"); setViewModal({ open: false, event: null }); }} className="bg-green-600 hover:bg-green-700 text-white"><CheckCircle className="w-4 h-4 mr-2" />Approve</Button>
                  </>
                )}
                <Button variant="outline" onClick={() => { setViewModal({ open: false, event: null }); openEdit(viewModal.event); }}><Edit className="w-4 h-4 mr-2" />Edit</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EventFormModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, event: null })}
        token={token}
        onSuccess={onRefresh}
        editEvent={editModal.event}
        adminMode
        businesses={businesses}
        markets={markets}
      />

      <EventFormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        token={token}
        onSuccess={onRefresh}
        adminMode
        businesses={businesses}
        markets={markets}
      />
    </div>
  );
};
